import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ButtonPrimaryV2,
  CheckboxList,
  Modal,
  RadioList,
  UI_BODY,
  UI_BODY_BOLD_SM,
  UI_CAPTION,
} from "oolib";
import { getTenant } from "@/TENANT_CONFIGS";
import { useGetQueryData } from "../../utils/react-query-hooks/general";
import { useGetTagsV3 } from "../../utils/react-query-hooks/tags";
import { isFeatureDisabledOnProd } from "../../utils/isFeatureDisabledOnProd";
import { DEEPINFRA, toDeepInfraSpec } from "../../utils/deepInfraModels";

/**
 * SignalPickerModal — staging/dev-only picker on the AI Annotate button.
 *
 * Renders the tenant's Signal tags as an all-checked-by-default checkbox
 * list plus a model choice for the run; on Done, calls
 * onConfirm({ allowedSignalTagIds, annotatorCall1Model }). Backend restricts
 * the annotator to that Signal subset, suppresses inductive Signal creation
 * for the run, and (when a model is picked) runs Call 1 on it instead of
 * the tenant's configured model.
 *
 * Gated off-prod by FEATURES_DISABLED_ON_PROD "aiAnnotateSignalPicker"
 * — do NOT render this modal on prod. Use useSignalPicker below to get
 * the correct enable check + modal-state coordination.
 */
const SIGNALS_SUBTHEME_TAG_ID = "signals";

// Model for this run. "default" leaves the tenant's configured Call 1 model
// in place (Claude Sonnet 4.5 unless the AI Engine Manager says otherwise);
// the DeepInfra entries send the backend's `custom` provider spec.
const DEFAULT_MODEL_VALUE = "default";
// Anthropic models offered besides the default. The backend's Anthropic
// client runs them with reasoning off, so Sonnet 5 here is the plain
// model at its lower per-token price, not the thinking variant.
const ANTHROPIC_OPTIONS = [{ value: "anthropic:claude-sonnet-5", modelName: "claude-sonnet-5", display: "Claude Sonnet 5 (no reasoning)" }];
const MODEL_OPTIONS = [
  { value: DEFAULT_MODEL_VALUE, display: "Claude Sonnet 4.5 (current default)" },
  ...ANTHROPIC_OPTIONS.map(({ value, display }) => ({ value, display })),
  ...DEEPINFRA.models.map((m) => ({ value: m.value, display: `${m.display} via DeepInfra` })),
];
const toAnnotatorCall1Model = (value) => {
  if (value === DEFAULT_MODEL_VALUE) return undefined;
  const anthropic = ANTHROPIC_OPTIONS.find((o) => o.value === value);
  if (anthropic) return { provider: "anthropic", modelName: anthropic.modelName };
  return toDeepInfraSpec(value);
};

const SignalPickerModal = ({ onConfirm, onClose }) => {
  const { data: subThemes = [], status: subThemesStatus } = useGetTagsV3("subThemes");

  const signalsSubThemeId = useMemo(() => {
    const st = (subThemes || []).find((s) => s?.tagId === SIGNALS_SUBTHEME_TAG_ID);
    return st?._id || null;
  }, [subThemes]);

  const { data: tags = [], status: tagsStatus } = useGetTagsV3("tags", {
    // Ask the discovery layer to filter server-side to only Signal children.
    // Falls back to empty results if the tenant has no `signals` subTheme
    // (query is enabled only once we've resolved the id).
    filters: signalsSubThemeId
      ? { "tags.subThemes.data._id": signalsSubThemeId }
      : {},
    enabled: !!signalsSubThemeId,
  });

  const options = useMemo(
    () =>
      (tags || []).map((t) => ({
        value: String(t._id),
        display: t?.main?.title || t?.tagId || String(t._id),
      })),
    [tags],
  );

  // All-checked by default. `selected` mirrors the CheckboxList value shape
  // (array of option objects); no localStorage — every open starts fresh.
  const [selected, setSelected] = useState(options);
  useEffect(() => {
    setSelected(options);
  }, [options]);

  const handleChange = (_id, next) => setSelected(next);

  const [modelValue, setModelValue] = useState(DEFAULT_MODEL_VALUE);

  const selectedCount = selected.length;
  const canConfirm = selectedCount > 0;

  const isLoading =
    subThemesStatus === "loading" || (!!signalsSubThemeId && tagsStatus === "loading");

  return (
    <Modal
      title="Set up this AI Annotation run"
      subtitle="Only the checked Signal tags will be considered by the annotator. New Signals will not be created during this run."
      onClose={onClose}
      desktopWidth="42rem"
      fitToContentHeight
      showActionPanel
      showCloseBtn
      onCloseText="Cancel"
      onConfirm={
        <ButtonPrimaryV2
          onClick={() =>
            canConfirm &&
            onConfirm({
              allowedSignalTagIds: selected.map((o) => o.value),
              annotatorCall1Model: toAnnotatorCall1Model(modelValue),
            })
          }
          disabled={!canConfirm}
        >
          Done
        </ButtonPrimaryV2>
      }
    >
      {isLoading ? (
        <UI_BODY>Loading Signals…</UI_BODY>
      ) : !signalsSubThemeId ? (
        <UI_BODY>
          This tenant has no <code>signals</code> sub-theme, so there is
          nothing to restrict. Close this and re-run without the picker.
        </UI_BODY>
      ) : options.length === 0 ? (
        <UI_BODY>
          The Signals sub-theme has no tags yet. Nothing to pick — close this
          and re-run without the picker.
        </UI_BODY>
      ) : (
        <>
          <CheckboxList
            id="signalPickerModal_selectedSignals"
            options={options}
            value={selected}
            onChange={handleChange}
            listType="vertical"
          />
          <UI_CAPTION
            color="greyColor60"
            style={{ display: "block", marginTop: "0.75rem" }}
          >
            {selectedCount}/{options.length} selected.
            {selectedCount === 0 && " Pick at least one to proceed."}
          </UI_CAPTION>
          <div style={{ marginTop: "1.5rem" }}>
            <UI_BODY_BOLD_SM style={{ display: "block", marginBottom: "0.5rem" }}>Model for this run</UI_BODY_BOLD_SM>
            <RadioList
              id="signalPickerModal_model"
              options={MODEL_OPTIONS}
              value={MODEL_OPTIONS.find((o) => o.value === modelValue)}
              onChange={(_id, next) => setModelValue(next?.value ?? DEFAULT_MODEL_VALUE)}
              listType="vertical"
            />
            <UI_CAPTION color="greyColor60" style={{ display: "block", marginTop: "0.5rem" }}>
              Applies to this run only; the tenant's configured model is unchanged.
            </UI_CAPTION>
          </div>
        </>
      )}
    </Modal>
  );
};

/**
 * useSignalPicker — coordinates the picker modal for a caller.
 *
 * Returns:
 *   - requestPickerThenRun(cb) — call from the AI Annotate button handler.
 *     If the picker is disabled (prod, or missing entry), invokes cb(undefined)
 *     synchronously. Otherwise opens the modal;
 *     cb({ allowedSignalTagIds, annotatorCall1Model }) fires when the user
 *     confirms (annotatorCall1Model is undefined when the default is kept).
 *   - SignalPickerModalNode — JSX to render alongside the button. Idiom
 *     matches the local `AIAnnotateLoaderOverlay` pattern; render it
 *     unconditionally in the parent's JSX.
 */
export const useSignalPicker = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const disabledFeatures = platformConfigs?.FEATURES_DISABLED_ON_PROD || [];
  const isProd = import.meta.env.VITE_ENV === "prod";
  const pickerEnabled = !isFeatureDisabledOnProd("aiAnnotateSignalPicker", {
    disabledFeatures,
    isProd,
    tenant: getTenant(),
  });

  // Holds the caller's continuation while the modal is open. Storing a fn
  // via useState needs the () => cb trick — useState calls its initializer.
  const [pendingCallback, setPendingCallback] = useState(null);

  const requestPickerThenRun = useCallback(
    (cb) => {
      if (!pickerEnabled) {
        cb(undefined);
        return;
      }
      setPendingCallback(() => cb);
    },
    [pickerEnabled],
  );

  const handleConfirm = (runOptions) => {
    const cb = pendingCallback;
    setPendingCallback(null);
    cb?.(runOptions);
  };

  const handleClose = () => setPendingCallback(null);

  const SignalPickerModalNode = pendingCallback ? (
    <SignalPickerModal onConfirm={handleConfirm} onClose={handleClose} />
  ) : null;

  return { pickerEnabled, requestPickerThenRun, SignalPickerModalNode };
};
